/**
 * Application entry point — wires the DOM to dispatch and the renderers.
 *
 * This is the only module that touches page-level elements by id. Everything it calls
 * is either pure (dispatch, search, format) or renders into a detached node, which is
 * what makes the rest of the app testable outside a browser.
 */
import type { Asset, Filters } from '../core/types.ts';
import { ASSETS } from '../core/universe.ts';
import { esc, plural } from '../core/format.ts';
import { dispatch } from './dispatch.ts';
import { SearchHistory } from './history.ts';
import { backendAvailable, enrichFromBackend } from './backend.ts';
import { buildScorecard } from './render/scorecard.ts';
import { buildListTable } from './render/list.ts';
import { buildOffUniverseCard } from './render/off-universe.ts';
import { initFeaturedCarousel, hideFeatured } from './render/featured.ts';

/** Assets discovered at runtime via backend enrichment, appended to the cached set. */
const runtimeAssets: Asset[] = [];
const allAssets = (): Asset[] => [...ASSETS, ...runtimeAssets];

const history = new SearchHistory();

function el<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

/** After results render, anchor the viewport on the query panel rather than mid-card. */
function scrollToQueryPanel(): void {
  const panel = document.querySelector('.query-panel');
  if (!panel) return;
  // 80ms lets the DOM settle before measuring.
  setTimeout(() => panel.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
}

function resultsRoot(): { section: HTMLElement; results: HTMLElement } | null {
  const section = el('results-section');
  const results = el('results');
  if (!section || !results) return null;
  section.classList.remove('hidden');
  hideFeatured();
  results.innerHTML = '';
  return { section, results };
}

function metaLine(text: string): HTMLElement {
  const meta = document.createElement('div');
  meta.className = 'results-meta';
  meta.textContent = text;
  return meta;
}

/** Re-render a freshly enriched asset in place of its pending scorecard. */
function onEnrich(asset: Asset, button: HTMLButtonElement): void {
  const original = button.innerHTML;
  button.disabled = true;
  button.textContent = 'Researching...';
  void enrichFromBackend(asset.name || asset.code).then((result) => {
    if (result?.code) {
      runtimeAssets.push(result);
      renderSingle(result);
    } else {
      button.disabled = false;
      button.innerHTML = original;
    }
  });
}

function renderSingle(asset: Asset): void {
  const root = resultsRoot();
  if (!root) return;
  root.results.appendChild(metaLine('Single-asset evaluation'));
  root.results.appendChild(buildScorecard(asset, onEnrich));
  scrollToQueryPanel();
}

function renderMulti(matches: Asset[], misses: string[]): void {
  const root = resultsRoot();
  if (!root) return;
  root.results.appendChild(
    metaLine(
      `${matches.length} ${plural(matches.length, 'asset')} evaluated · ${misses.length} off-universe`,
    ),
  );
  for (const a of matches) root.results.appendChild(buildScorecard(a, onEnrich));
  for (const m of misses) root.results.appendChild(offUniverseCard(m));
  scrollToQueryPanel();
}

function renderList(assets: Asset[], filters: Filters): void {
  const root = resultsRoot();
  if (!root) return;
  const n = assets.length;
  root.results.appendChild(metaLine(`Ranked output · ${n} ${plural(n, 'asset')} returned`));

  const disclosure = document.createElement('div');
  disclosure.className = 'universe-disclosure';
  disclosure.innerHTML =
    `Showing <strong>${n} highlighted ${plural(n, 'asset')}</strong> from a curated universe of approximately <strong>2,000 Atlas-eligible instruments</strong> across emerging markets.`;
  root.results.appendChild(disclosure);

  if (filters.limit && n < filters.limit) {
    const note = document.createElement('div');
    note.className = 'universe-notice';
    note.innerHTML =
      `Query requested top ${esc(filters.limit)}. ${n} qualifying ${plural(n, 'asset')} returned from the curated universe matching the filter criteria. Additional issuers outside the cached coverage set may be scored via direct ticker lookup with live backend enrichment.`;
    root.results.appendChild(note);
  }

  root.results.appendChild(buildListTable(assets, onEnrich));
  scrollToQueryPanel();
}

function offUniverseCard(query: string): HTMLElement {
  return buildOffUniverseCard(
    query,
    (q) => enrichFromBackend(q),
    (asset) => {
      runtimeAssets.push(asset);
      renderSingle(asset);
    },
  );
}

function renderOffUniverse(query: string): void {
  const root = resultsRoot();
  if (!root) return;
  root.results.appendChild(offUniverseCard(query));
}

/** Off-cache: go straight to the backend, showing a loading state while it researches. */
function renderOffCache(query: string): void {
  const root = resultsRoot();
  if (!root) return;
  const loading = document.createElement('div');
  loading.className = 'atlas-loading';
  loading.innerHTML =
    '<div class="spinner" aria-hidden="true"></div>' +
    `<div class="loading-text">Researching <strong>${esc(query)}</strong> …</div>` +
    '<div class="loading-sub">Atlas is gathering current market data, access regime, and coverage. This typically takes 10–20 seconds.</div>';
  loading.setAttribute('role', 'status');
  loading.setAttribute('aria-live', 'polite');
  root.results.appendChild(loading);
  scrollToQueryPanel();

  void enrichFromBackend(query).then((result) => {
    if (result?.code) {
      runtimeAssets.push(result);
      renderSingle(result);
    } else {
      renderOffUniverse(query);
    }
  });
}

export function runQuery(raw: string): void {
  const q = (raw || '').trim();
  if (!q) return;
  history.push(q);
  renderRecent();

  const result = dispatch(q, allAssets(), backendAvailable());
  switch (result.kind) {
    case 'single':
      return renderSingle(result.asset);
    case 'multi':
      return renderMulti(result.matches, result.misses);
    case 'list':
      return renderList(result.assets, result.filters);
    case 'off-cache':
      return renderOffCache(result.query);
    case 'off-universe':
      return renderOffUniverse(result.query);
    case 'empty':
      return;
  }
}

function renderRecent(): void {
  const row = el('recent-row');
  const wrap = el('recent-chips');
  if (!row || !wrap) return;
  if (history.entries.length === 0) {
    row.classList.add('hidden');
    return;
  }
  row.classList.remove('hidden');
  wrap.innerHTML = '';
  for (const q of history.entries) {
    const b = document.createElement('button');
    b.className = 'chip recent';
    b.type = 'button';
    b.textContent = q; // textContent, never innerHTML — history is user input
    b.addEventListener('click', () => {
      const input = el<HTMLInputElement>('search-input');
      if (input) input.value = q;
      runQuery(q);
    });
    wrap.appendChild(b);
  }
}

function init(): void {
  renderRecent();
  const input = el<HTMLInputElement>('search-input');
  const btn = el<HTMLButtonElement>('search-btn');

  btn?.addEventListener('click', () => runQuery(input?.value ?? ''));
  input?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') runQuery(input.value);
  });

  for (const chip of document.querySelectorAll<HTMLElement>('.chip[data-q]')) {
    chip.addEventListener('click', () => {
      const q = chip.dataset['q'] ?? '';
      if (input) input.value = q;
      runQuery(q);
    });
  }

  initFeaturedCarousel({
    runQuery,
    scrollToQueryPanel,
    pushHistory: (q) => {
      history.push(q);
      renderRecent();
    },
    // "Explore the Universe" bypasses parsePrompt deliberately, so the full cached
    // set is shown rather than whatever the label happens to parse to.
    renderUniverse: (assets) => renderList([...assets], { isListIntent: true }),
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
